import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { SiteService } from '@core/api/site.service';
import { FormatHelperService } from '@core/helper/format-helper.service';
import { MapService } from '@core/helper/map.service';
import * as fromApp from '@store';
import { selectIsMobileView } from '@store/app-config/app-config.selectors';
import { setMapControlDrawMode } from '@store/map-control/map-control.actions';
import { StatusEnum } from '@shared/constants/site_status.enum';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Site } from '@shared/models';
import { StatusCategoryEnum } from '@shared/constants/site_status_category.enum';
import { SiteStatusService } from '@core/helper/site-status.service';

interface StatusIcon {
  icon: string;
  color: string
}

interface Action {
  text: string;
  action: () => void;
}

@Component({
  selector: 'fc-site-card',
  templateUrl: './site-card.component.html',
  styleUrls: ['./site-card.component.scss'],
  standalone: false
})
export class SiteCardComponent implements OnInit {
  @Input() site: Site;

  @Output() updateAllSites = new EventEmitter<boolean>();

  destroyRef = inject(DestroyRef);

  isCardGray = false;
  statusColorStyle = '#201F1F';
  isMobile = false;
  isPendingSite = true;
  twentyYearMark = null;
  twentyYearProjection = 'N/A';
  projectedRevenue = '';
  isActionNeeded = false;
  statusIcon: StatusIcon = null;

  actionAndText: Action = null;

  constructor(
    public dialog: MatDialog,
    readonly store: Store<fromApp.AppState>,
    readonly siteService: SiteService,
    readonly siteStatusService: SiteStatusService,
    readonly formatHelper: FormatHelperService,
    readonly mapHelper: MapService,
    readonly router: Router
  ) {}

  ngOnInit() {
    // Count stands
    this.isPendingSite = this.siteStatusService.sitePendingStatusList.includes(this.site.status);
    this.isActionNeeded = [StatusEnum.DISTURBANCE_REPORTED.toString(),
      StatusEnum.REPORTING_REQUIRED.toString(),
      StatusEnum.DOCUMENT_REQUIRED.toString(),
      StatusEnum.REQUIRES_LANDOWNER_APPROVAL.toString(),
      StatusEnum.REQUIRES_LANDOWNER_REVISION.toString()].includes(this.site.status);

    switch (this.site.siteStatus.category) {
      case StatusCategoryEnum.ACTIVE:
        this.statusIcon = { icon: 'check-circle', color: 'green' };
        break;
      case StatusCategoryEnum.APPROVED:
        this.statusIcon = { icon: 'star', color: 'green' };
        break;
      default:
        this.statusIcon = null;
    }

    if (this.isActionNeeded) {
      this.statusIcon = { icon: 'circle-exclamation', color: 'orange' };
    }
    switch (this.site.status) {
      case StatusEnum.REPORTING_REQUIRED:
        this.actionAndText = {
          text: 'Complete Periodic Report',
          action: () => this.router.navigate(['account', 'site', this.site.id, 'report-response'])
        };
        break;
      case StatusEnum.DISTURBANCE_REPORTED:
        this.actionAndText = {
          text: 'Report On Disturbance',
          action: () => this.router.navigate(['account', 'site', this.site.id, 'report-response'], { queryParams: { reportType: 'Disturbance' } })
        };
        break;
      case StatusEnum.DOCUMENT_REQUIRED:
        this.actionAndText = {
          text: 'Provide Documentation',
          action: () => this.router.navigate(['account', 'site', this.site.id], { queryParams: { open: 'documents' } })
        };
        break;
      case StatusEnum.REQUIRES_LANDOWNER_APPROVAL:
        this.actionAndText = { text: 'Review Changes', action: () => this.onOpenSite() };
        break;
      case StatusEnum.REQUIRES_LANDOWNER_REVISION:
        this.actionAndText = { text: 'Revise Site', action: () => this.onOpenSite() };
        break;
      default:
        if (this.site.siteStatus.category == StatusCategoryEnum.PENDING) {
          this.actionAndText = { text: 'Continue', action: () => this.onOpenSite() };
        }
    }

    this.store
      .select(selectIsMobileView)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileViewMode => (this.isMobile = mobileViewMode));

    this.setCardBackgroundColor();
    this.setStatusColor();

    this.twentyYearMark = this.site.getYearMarkString();
    this.twentyYearProjection = this.formatHelper.formatMinMaxValuation(this.site.getTwentyYearProjection());

    this.projectedRevenue = this.formatHelper.formatMinMaxValuation(this.site.getAverageAnnualRevenue());
  }

  viewMap(): void {
    this.mapHelper.navigateToMap(this.router, this.site, 'site');
  }

  get totalAcreage(): string {
    return Math.round(this.site.area).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    });
  }

  onOpenSite(): void {
    this.store.dispatch(setMapControlDrawMode({ active: true }));
    this.mapHelper.navigateToMap(this.router, this.site, 'site');
  }

  onDeleteSite($event: any, site: Site): void {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Are you sure you want to delete this site?',
        line1: 'WARNING: This will permanently delete the site.',
        line2: 'This cannot be undone.',
        buttonTextYes: 'Delete Site',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.siteService.deleteSite(site.id).subscribe((response) => {
          this.updateAllSites.emit(true);
        })
      }
    });
  }

  setCardBackgroundColor(): void {
    switch (this.site.status) {
      case StatusEnum.UNSUBMITTED:
      case StatusEnum.UNDER_REVIEW:
        this.isCardGray = true;
        break;
      default:
        this.isCardGray = false;
    }
  }

  setStatusColor(): void {
    switch (this.site.status) {
      case StatusEnum.REPORTING_REQUIRED:
        this.statusColorStyle = '#FF9330';
        break;
      case StatusEnum.DISTURBANCE_REPORTED:
        this.statusColorStyle = '#ED5310';
        break;
      default:
        this.statusColorStyle = '#201F1F';
    }
  }

  projectionInfoBox(year: string): string {
    if (this.site?.enrollmentGroup?.acrProject.region) {
      return this.site.enrollmentGroup.acrProject.region.projectionInfoText(year);
    }
    return '';
  }

  navigate() {
    if (!this.isPendingSite) {
      this.router.navigate(['account', 'site', this.site.id]);
    }
  }

  protected readonly StatusEnum = StatusEnum;
}
